import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { ImportAutoConfiguratorDataResponse } from '@snap-types/actions/import-auto-configurator-data';
import { FeatureVisibility } from '@snap-types/models/enums';
import { BoxAlert } from '@ui/components/Alert';
import { JsonTree } from '@ui/components/JsonTree';
import { MasterSheetMismatch } from '@ui/features/alpha/components/MasterSheetMismatch';
import { isAutoConfiguratorSite } from '@ui/features/alpha/constants';
import { useImportAutoConfiguratorDataMutation } from '@ui/features/alpha/state/endpoints/import-auto-configurator-data';
import { useViewAutoConfiguratorDataQuery } from '@ui/features/alpha/state/endpoints/view-auto-configurator-data';
import {
  autoConfiguratorActions,
  autoConfiguratorSelectors,
} from '@ui/features/alpha/state/slice';
import { useCurrentSite } from '@ui/hooks/useCurrentSite';
import { useDispatch } from '@ui/hooks/useDispatch';
import { useSelector } from '@ui/hooks/useSelector';
import { useValidationToast } from '@ui/hooks/useToast';
import { normalizeError, summarizeValidationErrors } from '@ui/util/errors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

const autoConfiguratorImportSchema = z.object({
  file: z.custom<File>(
    (value): value is File => value instanceof File && value.size > 0,
    {
      message: 'Select a spreadsheet to continue.',
    },
  ),
});

/**
 *
 * @NOTE(shawk): 1/8/2026: This tool is only available for the ADF and ROC sites
 */
export function AutoConfiguratorImportPanel({
  visibility,
}: {
  visibility?: FeatureVisibility;
}) {
  const currentSite = useCurrentSite();
  const dispatch = useDispatch();
  const { genericSuccessToast, genericErrorToast } = useValidationToast();
  const lastImportResult = useSelector((state) =>
    autoConfiguratorSelectors.selectLastImportResult(state, currentSite.code),
  );
  const persistedVisibility = currentSite.snapSiteConfig.alphaVisible;

  const isVisibilityEnabled =
    (visibility ?? persistedVisibility) !== FeatureVisibility.NONE;

  const isSupportedSite =
    isAutoConfiguratorSite(currentSite.code) && isVisibilityEnabled;

  const isEnabledButUnsaved =
    visibility !== undefined &&
    visibility !== FeatureVisibility.NONE &&
    persistedVisibility === FeatureVisibility.NONE;

  const [importAutoConfiguratorData, mutation] =
    useImportAutoConfiguratorDataMutation();

  const [lastImportMismatches, setLastImportMismatches] = useState<
    ImportAutoConfiguratorDataResponse['mismatches'] | null
  >(null);

  const lastImportQuery = useViewAutoConfiguratorDataQuery(
    { siteCode: currentSite.code },
    { skip: !isSupportedSite },
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const normalizedError = normalizeError(
    mutation.error,
    'Auto Configurator import failed',
  );

  const validationSummary = useMemo(
    () => summarizeValidationErrors(normalizedError?.validationErrors),
    [normalizedError?.validationErrors],
  );

  const lastImportSummary = useMemo(() => {
    if (!lastImportResult) {
      return null;
    }

    const importedAt = new Date(lastImportResult.dataImport.createdAt);

    return {
      importedAt: importedAt.toLocaleString(),
      siteCode: lastImportResult.dataImport.siteCode,
      fileName: lastImportResult.dataImport.fileName,
      fileSizeBytes: lastImportResult.dataImport.fileSizeBytes,
      importInstanceKey: lastImportResult.dataImport.uuid,
      dataSets: lastImportResult.dataSets.map((set) => ({
        uuid: set.uuid,
        name: set.name,
        recordCount: countRecords(set.data),
        data: set.data,
      })),
    };
  }, [lastImportResult]);

  const isLastImportLoading =
    isSupportedSite &&
    (lastImportQuery.isLoading || lastImportQuery.isFetching) &&
    !lastImportSummary;

  const isLastImportRefreshing =
    !!lastImportSummary && lastImportQuery.isFetching;

  useEffect(() => {
    if (!isSupportedSite || !lastImportQuery.data) {
      return;
    }

    dispatch(
      autoConfiguratorActions.setLastImportResult({
        siteCode: currentSite.code,
        result: lastImportQuery.data.lastImport,
      }),
    );
  }, [currentSite.code, dispatch, isSupportedSite, lastImportQuery.data]);

  if (!isSupportedSite) {
    return null;
  }

  async function handleUpload() {
    const validationResult = autoConfiguratorImportSchema.safeParse({
      file: selectedFile,
    });

    if (!validationResult.success) {
      const flattenedErrors = validationResult.error.flatten();
      setFileError(
        flattenedErrors.fieldErrors.file?.[0] ??
          'Select a spreadsheet to continue.',
      );
      return;
    }

    setFileError(null);
    const file = validationResult.data.file;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await importAutoConfiguratorData({
        params: { siteCode: currentSite.code },
        body: formData,
      }).unwrap();

      setLastImportMismatches(result.mismatches);

      dispatch(
        autoConfiguratorActions.setLastImportResult({
          siteCode: currentSite.code,
          result,
        }),
      );

      genericSuccessToast(
        'Auto Configurator data uploaded',
        `${result.dataSets.length} data sets stored for ${result.dataImport.siteCode}.`,
      );

      void lastImportQuery.refetch();

      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      mutation.reset();
    } catch (error) {
      console.error('Auto Configurator import failed', error);
      setLastImportMismatches(null);
      genericErrorToast(
        'Import failed',
        'Check the spreadsheet and try again.',
      );
    }
  }

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} w="full">
      <Heading size="sm" mb={2} fontWeight="semibold">
        Auto Configurator Data
      </Heading>

      <Text fontSize="sm" color="snap.gray.700" mb={4}>
        Upload the spreadsheet to refresh pricing data for the Auto
        Configurator.
      </Text>

      {isEnabledButUnsaved ? (
        <Box mb={4}>
          <BoxAlert
            status="warning"
            title="Auto Configurator not saved"
            description="This setting is enabled in the form only. Save changes to persist it for this site."
          />
        </Box>
      ) : null}

      <Flex
        gap={4}
        align={{ base: 'stretch', md: 'flex-start' }}
        direction={{ base: 'column', md: 'row' }}
      >
        <FormControl isDisabled={mutation.isLoading} isInvalid={!!fileError}>
          <FormLabel hidden>Spreadsheet file</FormLabel>
          <Input
            ref={fileInputRef}
            required
            name="file"
            type="file"
            accept=".xlsx"
            aria-disabled={mutation.isLoading}
            onChange={(event) => {
              const file = event.currentTarget.files?.[0] ?? null;
              setSelectedFile(file);
              setFileError(null);
            }}
            sx={{
              '::file-selector-button': {
                height: 10,
                padding: 0,
                mr: 4,
                background: 'none',
                border: 'none',
                fontWeight: 'bold',
                cursor: 'pointer',
              },
            }}
          />
          {selectedFile && (
            <Text fontSize="xs" color="snap.gray.600" mt={1}>
              Selected: {selectedFile.name}
            </Text>
          )}
          <FormErrorMessage fontSize="xs">{fileError}</FormErrorMessage>
        </FormControl>

        <Button
          type="button"
          colorScheme="teal"
          minW={{ base: 'full', md: '200px' }}
          isLoading={mutation.isLoading}
          isDisabled={mutation.isLoading}
          onClick={handleUpload}
          rounded="sm"
        >
          Upload Spreadsheet
        </Button>
      </Flex>

      {normalizedError && (
        <Box mt={4}>
          <BoxAlert
            status="error"
            title={normalizedError.title}
            description={normalizedError.description}
          />

          {validationSummary.length > 0 && (
            <Box mt={3} borderWidth="1px" borderRadius="md" p={3}>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>
                Validation summary
              </Text>

              <UnorderedList spacing={1} ml={5} fontSize="sm">
                {validationSummary.map((line) => (
                  <ListItem key={line}>{line}</ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}
        </Box>
      )}

      {isLastImportLoading && (
        <Box mt={4} borderWidth="1px" borderRadius="md" p={3}>
          <Flex align="center" gap={3} color="snap.gray.600">
            <Spinner size="sm" color="snap.gray.500" />
            <Text fontSize="sm">Loading last import…</Text>
          </Flex>
        </Box>
      )}

      {lastImportMismatches && (
        <Box mt={4}>
          <MasterSheetMismatch mismatch={lastImportMismatches} />
        </Box>
      )}

      {lastImportSummary && (
        <Box mt={4} borderWidth="1px" borderRadius="md" p={3}>
          <Flex align="center" justify="space-between" mb={1}>
            <Text fontWeight="semibold">
              Last import ({lastImportSummary.importedAt})
            </Text>
            {isLastImportRefreshing ? (
              <Flex align="center" gap={2} fontSize="xs" color="snap.gray.600">
                <Spinner size="xs" color="snap.gray.500" />
                Refreshing…
              </Flex>
            ) : (
              <Text fontSize="xs" color="snap.gray.600" mb={2}>
                {lastImportSummary.fileName} ·{' '}
                {formatFileSize(lastImportSummary.fileSizeBytes)}
              </Text>
            )}
          </Flex>

          <Accordion
            key={lastImportSummary.importInstanceKey}
            allowMultiple
            reduceMotion
            borderColor="snap.gray.100"
            mx={-3}
          >
            {lastImportSummary.dataSets.map((dataSet) => (
              <AccordionItem key={dataSet.uuid} border="none">
                {({ isExpanded }) => (
                  <>
                    <AccordionButton
                      px={3}
                      py={2}
                      _expanded={{ bg: 'snap.gray.50' }}
                    >
                      <Flex
                        w="full"
                        align="center"
                        justify="space-between"
                        textAlign="left"
                        gap={3}
                      >
                        <Box>
                          <Text fontWeight="semibold" fontSize="sm">
                            {dataSet.name}
                          </Text>
                          <Text fontSize="xs" color="snap.gray.600">
                            {dataSet.recordCount} records
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </Flex>
                    </AccordionButton>

                    <AccordionPanel p={3}>
                      {isExpanded ? (
                        <Box
                          borderWidth="1px"
                          borderColor="snap.gray.100"
                          borderRadius="sm"
                          bg="snap.gray.50"
                        >
                          <JsonTree
                            key={`${lastImportSummary.importInstanceKey}-${dataSet.uuid}`}
                            label={dataSet.name}
                            value={dataSet.data}
                            renderChunkSize={200}
                            maxHeight={420}
                            startExpanded
                          />
                        </Box>
                      ) : (
                        <Text fontSize="xs" color="snap.gray.600">
                          Expand to preview the stored data.
                        </Text>
                      )}
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            ))}
          </Accordion>
        </Box>
      )}
    </Box>
  );
}

function countRecords(data: unknown) {
  if (Array.isArray(data)) {
    return data.length;
  }

  if (data && typeof data === 'object') {
    return Object.keys(data as Record<string, unknown>).length;
  }

  return 0;
}

function formatFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return 'Unknown size';
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const shouldHideDecimals = value >= 100;
  const shouldUseSingleDecimal = value >= 10 && value < 100;

  let decimalPlaces = 2;
  if (shouldHideDecimals) {
    decimalPlaces = 0;
  } else if (shouldUseSingleDecimal) {
    decimalPlaces = 1;
  }

  return `${value.toFixed(decimalPlaces)} ${units[unitIndex]}`;
}
